/**
 * Engagement-triggered auth prompt state.
 *
 * Tracks unique regions a visitor has viewed and decides when to surface
 * the `AuthPromptDialog`. Counting *unique* regions (not page loads)
 * keeps refreshes and back-button loops from tripping the gate; the
 * prompt is meant to fire when the visitor is genuinely exploring
 * multiple breaks, not when they're bouncing around one.
 *
 * State lives in localStorage (mirrors `welcomeFlag.ts`) — a full
 * Recoil atom would be overkill for non-reactive, cross-session state.
 */
const REGIONS_KEY = "authEngagementRegions";
const DISMISSED_KEY = "authEngagementDismissedAt";
const CONVERTED_KEY = "authEngagementConverted";

export const ENGAGEMENT_THRESHOLD = 3;
export const ENGAGEMENT_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // private mode / blocked storage — no-op; we'll just never prompt
  }
}

function readRegions(): string[] {
  const raw = safeGet(REGIONS_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === "string") : [];
  } catch {
    return [];
  }
}

function isInCooldown(now: number): boolean {
  const raw = safeGet(DISMISSED_KEY);
  if (!raw) return false;
  const dismissedAt = Number(raw);
  if (!Number.isFinite(dismissedAt)) return false;
  return now - dismissedAt < ENGAGEMENT_COOLDOWN_MS;
}

/**
 * Record a region view. Returns `shouldPrompt: true` at most once per
 * cooldown window, when the unique-region count crosses the threshold
 * and the visitor is not yet converted or cooling down.
 */
export function recordRegionView(
  regionId: string,
  now: number = Date.now(),
): { shouldPrompt: boolean } {
  if (!regionId) return { shouldPrompt: false };
  if (safeGet(CONVERTED_KEY) === "true") return { shouldPrompt: false };
  if (isInCooldown(now)) return { shouldPrompt: false };

  const seen = readRegions();
  if (!seen.includes(regionId)) {
    seen.push(regionId);
    safeSet(REGIONS_KEY, JSON.stringify(seen));
  }
  return { shouldPrompt: seen.length >= ENGAGEMENT_THRESHOLD };
}

/**
 * Called when the user dismisses the prompt. Starts the cooldown so we
 * don't ask again immediately on the next region change.
 */
export function markPromptDismissed(now: number = Date.now()): void {
  safeSet(DISMISSED_KEY, String(now));
}

/**
 * Called once the user signs in. Prevents the prompt from ever firing
 * again, even if they sign out later.
 */
export function markPromptConverted(): void {
  safeSet(CONVERTED_KEY, "true");
}

/** Test helper — wipes all engagement state. */
export function __resetEngagementState(): void {
  try {
    localStorage.removeItem(REGIONS_KEY);
    localStorage.removeItem(DISMISSED_KEY);
    localStorage.removeItem(CONVERTED_KEY);
  } catch {
    // ignore
  }
}
